/**
 * Where to get permitted, what's needed to start, and the roles a food business must fill (owner, 2026-10-02: "And where
 * to get permitted and what permits are needed to start up and all roles if others exist").
 *
 * Two more rule shapes, stored as rule values on the jurisdiction nodes (jurisdiction.ts) and inherited like every rule:
 *  - `permit.<id>`: a PermitEntry: the permit or step, the issuing agency, where to apply (portal, office, hours, phone:
 *    each sourced), what it needs first (`requires`: the dependency order), what to bring, the fee ONLY when sourced, the
 *    renewal cycle and the deadline (deadline shapes), and its scope (permit types). A gap says "confirm with <agency>".
 *  - `role.<id>`: a RoleRequirement: the role (person in charge, certified food protection manager, food handler cards,
 *    allergen awareness, and any other a place must have), its certification, deadline, who needs it, and how it's covered
 *    (every shift, at least one employed, every food worker, present all hours).
 *
 * Nevada's "Open legally" path is generated from its catalog in dependency order (setup.ts setupForPlace). Utah's path is
 * unchanged (setup.ts setupFor); its catalog is an added view whose dependency order is tested against that path.
 */
import type { Credential, IsoDate, PermitType } from "./model.ts";
import { keysIn, registerRules, resolveIn, statusOf, type Place, type RuleValue, type World } from "./jurisdiction.ts";
import { ROLE_RULES } from "./states/roles-data.ts";
import { businessSetupFor, type SetupStep } from "./setup.ts";
import type { GuideStep } from "./states/nv-opening.ts";
import { isStateLoaded, stateModule } from "./states/access.ts";
import type { UsState } from "./states/types.ts";
import type { Source } from "./states/types.ts";
import { deadlineLine, type Deadline } from "./states/shapes.ts";

/** A non-Utah state's opening steps, from its loaded bundle (these run only for loaded states). */
const OPEN = {
  NV: () => stateModule<typeof import("./states/nv-opening.ts")>("NV", "opening"),
  CA: () => stateModule<typeof import("./states/ca-opening.ts")>("CA", "opening"),
  CAL: () => stateModule<typeof import("./states/ca-opening-local.ts")>("CA", "openingLocal"),
  NY: () => stateModule<typeof import("./states/ny-opening.ts")>("NY", "opening"),
  OR: () => stateModule<typeof import("./states/or-opening.ts")>("OR", "opening"),
  FL: () => stateModule<typeof import("./states/fl-opening.ts")>("FL", "opening"),
  TX: () => stateModule<typeof import("./states/tx-opening.ts")>("TX", "opening"),
  MA: () => stateModule<typeof import("./states/ma-opening.ts")>("MA", "opening"),
};
const memo = <T,>(f: () => T): (() => T) => { let v: T | undefined; return () => (v ??= f()); };

// ── Shapes ───────────────────────────────────────────────────────────────────────────────────────────────────────────

export type PermitKind =
  | "health_permit" | "plan_review" | "inspection" | "business_license" | "state_business_license" | "seller_permit" | "tax_id" | "mobile_unit" | "commissary_agreement"
  | "temporary_event" | "cottage_registration" | "mehk" | "alcohol" | "fire" | "certification" | "posting" | "employer" | "other";

export interface PermitEntry {
  id: string;
  kind: PermitKind;
  title: string;
  why: string;
  /** The issuing agency (or "You" for a step the owner does). */
  agency: string;
  /** Where to apply, each part only when sourced. */
  where: { phone?: string; portal?: string; office?: string; hours?: string };
  /** The permit ids that come first (sourced order: plans before building, the health permit before a city license...). */
  requires: string[];
  /** Tie-break inside the dependency order (setup.ts order numbers). */
  order: number;
  phase: "start" | "hire";
  bring: string[];
  /** A fee: shown only with its source (never a guess). */
  fee?: { amount: string; source?: Source };
  due?: Deadline;
  renew?: Deadline;
  /** The local authority does this one: a confirmed authority's own contact is used (the state program's counties). */
  local?: boolean;
  askAPro?: boolean;
  verifyLocal: boolean;
  /** Free-text deadline from older data (Utah's setup words). */
  deadlineText?: string;
}

export type RoleCoverage = "present_all_hours" | "every_shift" | "at_least_one_employed" | "every_food_worker" | "pic_shows_knowledge" | "every_alcohol_server" | "only_if_process" | "one_on_shift";

export interface RoleRequirement {
  id: "pic" | "cfpm" | "food_handler" | "allergen_awareness" | "alcohol_server" | "first_aid" | "special_process" | string;
  title: string;
  required: boolean;
  /** The credential that counts (Credential.kind), or a plain description. */
  certification?: Credential["kind"] | string;
  coverage?: RoleCoverage;
  who: string;
  due?: Deadline;
  renew?: Deadline;
  riskLevels?: string[];
  /** Applies only at places with at least this many seats (Massachusetts choke-saving: 25). Seats not answered = not applied. */
  minSeats?: number;
  note?: string;
}

// ── The fee rule: never a guess ──────────────────────────────────────────────────────────────────────────────────────

/** The fee line, or null when the fee has no source (an unsourced fee never shows). */
export function permitFeeLine(e: Pick<PermitEntry, "fee">): string | null {
  const f = e.fee;
  return f && f.source?.citation && /^https?:\/\//.test(f.source.url ?? "") ? `Fee: ${f.amount} (${f.source.citation})` : null;
}

// ── Nevada's catalog (from the opening guide data, with the sourced dependency order) ────────────────────────────────

/**
 * What each Nevada step needs first, from the rules: plans before the permit (NRS 580.260; SNHD 8-201.1; Washoe 090.005(B));
 * the inspection after the application (NRS 580.400(3); SNHD 8-303.1(C); Washoe 020.020); posting after the permit
 * (NRS 580.400(5)(b)); the grade card after the first inspection (SNHD 8-403.2(A)); a city or county license only after
 * the health permit or on condition of it (NRS 580.410); a truck's permit after its plans and commissary agreement (SNHD 10-101).
 */
const NV_REQUIRES: Record<string, string[]> = {
  "food-permit": ["plan-review"],
  "preopening-inspection": ["food-permit"],
  "city-business-license": ["food-permit"],
  "post-permit": ["preopening-inspection"],
  "grade-card": ["preopening-inspection"],
  "mfb-permit": ["mfb-plan-review", "commissary-agreement"],
};

const KIND: Record<string, PermitKind> = {
  "state-registration": "state_business_license", ein: "tax_id", "sales-tax-license": "seller_permit", "city-business-license": "business_license",
  "plan-review": "plan_review", "food-permit": "health_permit", "preopening-inspection": "inspection", cfsm: "certification", "food-handler-self": "certification",
  "food-handler-staff": "certification", "post-permit": "posting", "grade-card": "posting", "mfb-plan-review": "plan_review", "commissary-agreement": "commissary_agreement",
  "mfb-permit": "mobile_unit", "temp-event-permit": "temporary_event", "cottage-registration": "cottage_registration", "no-home-food-business": "other",
  "mehk-permit": "mehk", "change-of-ownership": "health_permit", "reinspection-request": "inspection", "mfb-fire-inspection": "fire", "restaurant-tax": "seller_permit", "temp-sales-tax": "seller_permit", "workers-comp": "employer", "ui-account": "employer",
  "withholding-account": "employer", "i9-w4": "employer", "new-hire-report": "employer", "cfsm-replace": "certification", "choose-path": "other", "preopening": "inspection", "prop65-signs": "posting", "alcohol-license": "alcohol", "fire-occupancy": "fire",
};


function guideRule(node: string, g: GuideStep, opts: { local?: boolean; office?: string; requires?: Record<string, string[]>; minEmployees?: number } = {}): RuleValue<PermitEntry> {
  const entry: PermitEntry = {
    id: g.id, kind: KIND[g.id] ?? "other", title: g.title, why: g.why, agency: g.issuer,
    where: { ...(g.phone ? { phone: g.phone } : {}), ...(opts.office && g.phone ? { office: opts.office } : {}) },
    requires: (opts.requires ?? NV_REQUIRES)[g.id] ?? [], order: g.order, phase: g.phase, bring: g.bring, ...(g.due ? { due: g.due } : {}),
    ...(opts.local ? { local: true } : {}), ...(g.askAPro ? { askAPro: true } : {}), verifyLocal: g.verifyLocal,
  };
  return { id: `${node}/permit.${g.id}`, version: 1, key: `permit.${g.id}`, node, status: "law", value: entry, source: { citation: g.citation, url: g.link, ...(g.quote ? { quote: g.quote } : {}), lastChecked: g.lastChecked }, lastChecked: g.lastChecked, scope: { permits: g.permitTypes, ...(opts.minEmployees ? { minEmployees: opts.minEmployees } : {}) } };
}

const NV_RULES = memo((): RuleValue[] => { const NV_OPENING = OPEN.NV().NV_OPENING; return [
  ...NV_OPENING.business.map((g) => guideRule("NV", g)),
  ...NV_OPENING.stateProgram.map((g) => guideRule("NV", g, { local: true })),
  // Offices, portals and hours come from the contact directory per purpose (directory.ts): SNHD's plan review office is
  // not its main office, so the catalog doesn't guess one address for every step.
  ...NV_OPENING.authorities.snhd!.map((g) => guideRule("NV/snhd", g)),
  ...NV_OPENING.authorities.nnph!.map((g) => guideRule("NV/nnph", g)),
  ...NV_OPENING.authorities["carson-city-hhs"]!.map((g) => guideRule("NV/carson-city-hhs", g)),
  ...NV_OPENING.authorities.cnhd!.map((g) => guideRule("NV/cnhd", g)),
  // Washoe's truck and event steps weren't read (nevada.md): unknown, so the state program's never stand in for them.
  ...(["mfb-permit", "temp-event-permit"] as const).map((id): RuleValue => ({ id: `NV/nnph/permit.${id}`, version: 1, key: `permit.${id}`, node: "NV/nnph", status: "unknown", tried: "Washoe food regulations not read for mobile units and temporary events (nevada.md)", lastChecked: "2026-10-02" })),
]; });

// ── California's catalog: the statewide steps (ca-opening.ts), then each county's own (its grade card, its own card) ──

/** California's order, from the rules: plans approved before building (HSC 114380), no opening without the permit (114381). */
const CA_REQUIRES: Record<string, string[]> = {
  "food-permit": ["plan-review"], "mfb-permit": ["plan-review", "commissary-agreement"], "post-permit": ["food-permit"], "prop65-signs": ["food-permit"], "grade-card": ["food-permit"],
};
const CA_RULES = memo((): RuleValue[] => [
  ...OPEN.CA().CA_STATE_STEPS.map((g) => guideRule("CA", g, { local: !!g.local, requires: CA_REQUIRES, ...(g.minEmployees ? { minEmployees: g.minEmployees } : {}) })),
  ...OPEN.CAL().CA_GRADE_STEPS.map(({ node, step }) => guideRule(node, step, { requires: CA_REQUIRES })),
  ...OPEN.CAL().CA_OWN_CARD_STEPS.map(({ node, step }) => guideRule(node, step, { requires: CA_REQUIRES })),
]);

// ── New York's catalog: the statewide steps, then NYC's and each county's own ──

/** New York's order: coverage before the permit (NYSDOH), the permit before the signs (14-1.190). Plans only if the office asks. */
const NY_REQUIRES: Record<string, string[]> = {
  "food-permit": ["workers-comp"], "mfb-permit": ["commissary-agreement", "workers-comp"], "temp-event-permit": ["workers-comp"], "post-permit": ["food-permit"], "grade-card": ["food-permit"],
};
const NY_RULES = memo((): RuleValue[] => [
  ...OPEN.NY().NY_STATE_STEPS.map((g) => guideRule("NY", g, { local: !!g.local, requires: NY_REQUIRES })),
  ...OPEN.NY().NY_NYC_STEPS.map((g) => guideRule("NY/nyc", g, { requires: NY_REQUIRES })),
  ...OPEN.NY().NY_COUNTY_STEPS.map(({ node, step }) => guideRule(node, step, { requires: NY_REQUIRES })),
]);

// ── Oregon's catalog: the statewide steps, each run by the county office for OHA ──

/** Oregon's order: plans before the license (ORS 624), the license before the signs (ORS 624.020(5)). */
const OR_REQUIRES: Record<string, string[]> = { "food-permit": ["plan-review"], "mfb-permit": ["plan-review"], "post-permit": ["food-permit"] };
const OR_RULES = memo((): RuleValue[] => OPEN.OR().OR_STATE_STEPS.map((g) => guideRule("OR", g, { local: !!g.local, requires: OR_REQUIRES })));

// ── Florida's catalog: the state's steps, all DBPR's (509.032(7)) ──
const FL_REQUIRES: Record<string, string[]> = { "food-permit": ["plan-review"], "mfb-permit": ["plan-review", "commissary-agreement"], "post-permit": ["food-permit"] };
const FL_RULES = memo((): RuleValue[] => OPEN.FL().FL_STATE_STEPS.map((g) => guideRule("FL", g, { requires: FL_REQUIRES })));

// ── Texas's catalog: the state's steps, run by the local office (or DSHS) ──
const TX_REQUIRES: Record<string, string[]> = { "food-permit": ["plan-review"], "post-permit": ["food-permit"] };
const TX_RULES = memo((): RuleValue[] => OPEN.TX().TX_STATE_STEPS.map((g) => guideRule("TX", g, { local: !!g.local, requires: TX_REQUIRES })));

// ── Massachusetts's catalog: the state's steps, run by each town's board of health ──
const MA_REQUIRES: Record<string, string[]> = { "food-permit": ["plan-review"], "mfb-permit": ["plan-review"], "post-permit": ["food-permit"] };
const MA_RULES = memo((): RuleValue[] => OPEN.MA().MA_STATE_STEPS.map((g) => guideRule("MA", g, { local: !!g.local, requires: MA_REQUIRES })));

// ── Utah's catalog (an added view of the existing statewide path) ────────────────────────────────────────────────────

/** Utah's order, from the rules: plans before the permit and the pre-opening inspection (Food Code 8-201.11, 8-302, adopted by R392-100). */
const UT_REQUIRES: Record<string, string[]> = {
  "food-permit": ["plan-review"], "preopening-inspection": ["plan-review"], "post-permit": ["food-permit"],
  "mfb-permit": ["mfb-plan-review", "commissary-agreement"], "mfb-fire-inspection": ["mfb-plan-review"],
};
function utRules(): RuleValue[] {
  return businessSetupFor("UT").map((s: SetupStep): RuleValue<PermitEntry> => ({
    id: `UT/permit.${s.id}`, version: 1, key: `permit.${s.id}`, node: "UT", status: statusOf(s.citation, s, "UT"),
    value: { id: s.id, kind: KIND[s.id] ?? "other", title: s.title, why: s.why, agency: s.issuer, where: {}, requires: UT_REQUIRES[s.id] ?? [], order: s.order, phase: s.phase, bring: [], verifyLocal: s.verifyLocal, ...(s.deadline ? { deadlineText: s.deadline } : {}) },
    source: { citation: s.citation, url: s.link, lastChecked: "2026-09-27" }, lastChecked: "2026-09-27", scope: { permits: s.permitTypes },
  }));
}

// Registered lazily: the world is built on first use, after every module (setup.ts included) has loaded.
const RULES_OF: [Exclude<UsState, "UT">, () => RuleValue[]][] = [["NV", NV_RULES], ["CA", CA_RULES], ["NY", NY_RULES], ["OR", OR_RULES], ["FL", FL_RULES], ["TX", TX_RULES], ["MA", MA_RULES]];
// Only the loaded states' catalogs (the world rebuilds when another loads); with every state loaded the list is the same as before.
registerRules(() => [...RULES_OF.filter(([st]) => isStateLoaded(st)).flatMap(([, rules]) => rules()), ...utRules(), ...ROLE_RULES]);

// ── Reading them ─────────────────────────────────────────────────────────────────────────────────────────────────────

/** Topological order by `requires`, ties by `order` then catalog position. A cycle or a missing prerequisite is reported. */
export function dependencyOrder<T extends Pick<PermitEntry, "id" | "requires" | "order">>(items: readonly T[]): { ordered: T[]; problems: string[] } {
  const byId = new Map(items.map((x) => [x.id, x]));
  const problems: string[] = [];
  const done = new Set<string>();
  const ordered: T[] = [];
  const pending = [...items].map((x, i) => ({ x, i }));
  while (pending.length) {
    const ready = pending.filter(({ x }) => x.requires.every((r) => done.has(r) || !byId.has(r)));
    if (!ready.length) { problems.push(`cycle among: ${pending.map(({ x }) => x.id).join(", ")}`); ordered.push(...pending.map(({ x }) => x)); break; }
    ready.sort((a, b) => a.x.order - b.x.order || a.i - b.i);
    const next = ready[0]!;
    ordered.push(next.x); done.add(next.x.id); pending.splice(pending.indexOf(next), 1);
  }
  return { ordered, problems };
}

export interface PermitCatalogItem { entry: PermitEntry; status: RuleValue["status"]; citation: string; url: string; quote?: string; node: string; permits: PermitType[] }

/** The permit catalog for a place and permit type, in dependency order (each entry resolved through the jurisdiction chain). */
export function permitCatalog(world: World, place: Place, permit: PermitType, o: { date?: IsoDate } = {}): PermitCatalogItem[] {
  const items: PermitCatalogItem[] = [];
  for (const key of keysIn(world, place, "permit.")) {
    const r = resolveIn<PermitEntry>(world, key, place, { date: o.date, scope: { permit } });
    if (!r.rule || r.status === "unknown" || !r.value) continue;
    items.push({ entry: r.value, status: r.status, citation: r.rule.source?.citation ?? "", url: r.rule.source?.url ?? "", ...(r.rule.source?.quote ? { quote: r.rule.source.quote } : {}), node: r.rule.node, permits: r.rule.scope?.permits ?? [permit] });
  }
  const { ordered } = dependencyOrder(items.map((x) => ({ ...x, id: x.entry.id, requires: x.entry.requires, order: x.entry.order })));
  return ordered.map(({ entry, status, citation, url, quote, node, permits }) => ({ entry, status, citation, url, ...(quote ? { quote } : {}), node, permits }));
}

/** The roles in force at a place (role.*), each resolved (guidance labeled), unknowns kept so the screen can say "confirm". */
export function rolesAt(world: World, place: Place, o: { date?: IsoDate } = {}) {
  const keys = [...new Set([...keysIn(world, place, "role."), ...world.rules.filter((r) => r.node === "US" && r.key.startsWith("role.")).map((r) => r.key)])];
  return keys.map((k) => resolveIn<RoleRequirement>(world, k, place, o));
}

/** The certificates every place can add on People (the two every state's rules read). */
export const BASE_CREDENTIAL_KINDS: readonly { kind: Credential["kind"]; label: string }[] = [
  { kind: "food_handler_permit", label: "Food handler permit" },
  { kind: "certified_food_safety_manager", label: "Food safety manager" },
];
/** Labels for the certificates a place's law adds (roles-data.ts `certification`). */
export const EXTRA_CREDENTIAL_LABEL: Partial<Record<Credential["kind"], string>> = { allergen_awareness: "Allergen awareness certificate", choking_first_aid: "Choking first aid (trained)" };

/**
 * The certificate types People offers at this place (fix/inspector-crosswalk): the base two, plus each one the place's law
 * requires (a law role's `certification`), and one tied to seating only where the place said it seats that many (Massachusetts
 * choke-saving at 25+). Asked only where it applies: one entry per person, never a daily tap.
 */
export function credentialKindsAt(world: World, place: Place, seats?: number): { kind: Credential["kind"]; label: string; citation?: string }[] {
  const out: { kind: Credential["kind"]; label: string; citation?: string }[] = [...BASE_CREDENTIAL_KINDS];
  for (const r of rolesAt(world, place)) {
    const v = r.value;
    const kind = v?.certification as Credential["kind"] | undefined;
    if (!v || r.status !== "law" || !kind || !EXTRA_CREDENTIAL_LABEL[kind] || out.some((x) => x.kind === kind)) continue;
    if (v.minSeats !== undefined && !(seats !== undefined && seats >= v.minSeats)) continue;
    out.push({ kind, label: EXTRA_CREDENTIAL_LABEL[kind]!, ...(r.rule?.source?.citation ? { citation: r.rule.source.citation } : {}) });
  }
  return out;
}

/** The seat count a rule here depends on (Massachusetts choke-saving: 25), with its rule; null = no question to ask. */
export function seatQuestionAt(world: World, place: Place): { minSeats: number; title: string; citation: string } | null {
  for (const r of rolesAt(world, place)) {
    const v = r.value;
    if (v && r.status === "law" && v.minSeats !== undefined) return { minSeats: v.minSeats, title: v.title, citation: r.rule?.source?.citation ?? "" };
  }
  return null;
}

export interface RoleGap { role: string; line: string; citation: string; status: RuleValue["status"] }

const validCred = (c: Pick<Credential, "kind" | "expiresOn">, kind: string, today: IsoDate) => c.kind === kind && (!c.expiresOn || c.expiresOn >= today);

/**
 * "Who's carrying what" gaps: each required role not covered right now. A flag for the manager, never a block (ruleshapes.ts P1).
 * every_shift: the person in charge on shift holds the certificate ("No certified manager on this shift"); at_least_one_employed:
 * someone on the team does; every_food_worker: each food worker on shift does; present_all_hours: someone is in charge.
 */
export function roleGaps(world: World, place: Place, o: { today: IsoDate; onShift: readonly { name: string; isPersonInCharge: boolean; credentials: readonly Pick<Credential, "kind" | "expiresOn">[] }[]; team: readonly { credentials: readonly Pick<Credential, "kind" | "expiresOn">[] }[]; permit?: PermitType; /** Location.seats; absent = not answered. */ seats?: number }): RoleGap[] {
  const out: RoleGap[] = [];
  for (const r of rolesAt(world, place, { date: o.today })) {
    const v = r.value;
    if (!v || !v.required || r.status === "unknown") continue;
    const citation = r.rule?.source?.citation ?? "";
    const pic = o.onShift.find((p) => p.isPersonInCharge);
    const cert = typeof v.certification === "string" && /_/.test(v.certification) ? v.certification : undefined;
    // A certificate the law here doesn't ask for is never a gap: the FDA model alone (guidance) can't make "No certified manager on
    // this shift" true (New York outside NYC and its certificate counties; 10 NYCRR 14-1.70 to 14-1.73).
    if (cert && r.status === "guidance") continue;
    // A role tied to seating (Massachusetts choke-saving at 25+) applies only where the place said it seats that many.
    if (v.minSeats !== undefined && !(o.seats !== undefined && o.seats >= v.minSeats)) continue;
    // one_on_shift: someone on this shift holds the certificate (Massachusetts: a choke-saving-trained employee on site).
    if (v.coverage === "one_on_shift" && cert && o.onShift.length && !o.onShift.some((p) => p.credentials.some((c) => validCred(c, cert, o.today))))
      out.push({ role: v.id, line: `No one on this shift: ${v.title}`, citation, status: r.status });
    if (v.coverage === "present_all_hours" && o.onShift.length && !pic) out.push({ role: v.id, line: "No person in charge on this shift", citation, status: r.status });
    if (v.coverage === "every_shift" && cert && o.onShift.length && o.permit !== "temporary_event" && !(pic && pic.credentials.some((c) => validCred(c, cert, o.today))))
      out.push({ role: v.id, line: "No certified manager on this shift", citation, status: r.status });
    if (v.coverage === "at_least_one_employed" && cert && !o.team.some((p) => p.credentials.some((c) => validCred(c, cert, o.today))))
      out.push({ role: v.id, line: v.id === "cfpm" ? "No certified food protection manager on the team" : `Not on the team yet: ${v.title}`, citation, status: r.status });
    // A card due "within N days" of starting (Utah) isn't a gap inside its window: the onboarding countdown handles it. A card
    // due before the first shift (SNHD) is a gap from day one.
    if (v.coverage === "every_food_worker" && cert && v.due?.kind !== "within_days") {
      const missing = o.onShift.filter((p) => !p.credentials.some((c) => validCred(c, cert, o.today) || validCred(c, "certified_food_safety_manager", o.today)));
      if (missing.length) out.push({ role: v.id, line: `${missing.map((p) => p.name).join(", ")}: ${v.title} not on file`, citation, status: r.status });
    }
  }
  return out;
}

/** Nevada's opening steps from the catalog (setup.ts setupForPlace), as SetupSteps. */
export function catalogSetupSteps(world: World, place: Place, permit: PermitType, opts: { hiring?: boolean; authority?: { name: string; phone?: string } } = {}): SetupStep[] {
  const hiring = opts.hiring ?? true;
  return permitCatalog(world, place, permit)
    .filter((c) => hiring || c.entry.phase === "start")
    .map(({ entry: e, citation, url, node, permits }) => {
      // The state program's steps are the local authority's job: a confirmed authority's own contact (its field office).
      const local = e.local && node === place.state && opts.authority?.phone;
      // The authority's own step with no phone of its own (CNHD's forms): its office for this county.
      const phone = local ? opts.authority!.phone : e.where.phone ?? (opts.authority && e.agency === opts.authority.name ? opts.authority.phone : undefined);
      return {
        id: e.id, order: e.order, phase: e.phase, title: e.title, why: e.why, issuer: local ? opts.authority!.name : e.agency, link: url, citation,
        source: "docs/source/research/nevada.md", permitTypes: permits, stages: [1, 2, 3, 4, 5], verifyLocal: e.verifyLocal,
        ...(e.due ? { due: e.due, deadline: deadlineLine(e.due) } : {}), ...(phone ? { phone } : {}), bring: e.bring,
      };
    });
}

